import './filesDistributionByPCILabels.less'
import {
  ACTION_FETCH_PCI_FILE_DISTRIBUTION,
  fetchPCIFileDistribution
} from '../../../complianceDashboardSlice'
import LoaderView from '../../../../../../components/Loader/loader'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { RootState } from '../../../../../../rootReducer'
import {
  UNIQUE_WIDGET_INDEX,
  getUniqueWidgetIndex,
  ChartColors
} from '../../../../mainDashboardv2/constants'
import { mockPCIFileDistribution } from '../../mocks'
import WidgetTitle from '../../../../../../components/WidgetTitle'
import Card from '../../../../../accessControl/components/card'
import ChartPie, { ChartPieItem } from '../../../../../../components/ChartPie/chartPie'
import { AttributeSetData, PCIFileDistributionData } from '../../../queries'
import historyService from '../../../../../../services/historyService'
import { getUpdatedUrl } from '../../../../../../utils/urlUtil'
import {
  URL_ATTRIBUTES_SET,
  URL_DATA_SOURCES,
  URL_INSIGHTS,
  URL_LABEL_MANAGEMENT,
  URL_LABEL_SETS,
  URL_LABELS,
  URL_OVERVIEW
} from '../../../../../../constants'
import { hasData } from '../../helper'
import { EmptyState } from '../../emptyState/emptyState'
import EmptyStateIcon from '../../../../../../assets/inlineSvg/labels-empty-state-icon.svg'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import {
  ComplianceDashboardTypes,
  HIPAA_ATTRIBUTE_SET_NAME_FOR_LABELS,
  PCI_ATTRIBUTE_SET_NAME_FOR_LABELS
} from '../../../constants'
import { Typography, Icon, Tooltip, Button } from '@lightbeamai/design-system'
import React, { useEffect, useMemo } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'
import { connect } from 'react-redux'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDashboardType?: string
}

interface StateProps {
  data?: PCIFileDistributionData | null
  attributeSetData?: AttributeSetData | null
  showLoader: boolean
}

interface DispatchProps {
  fetchPCIFileDistribution: (params: {
    attributeSetName: string
    [UNIQUE_WIDGET_INDEX]?: string
  }) => void
}

interface ListItem {
  key: string
  name: string
  value: number
  color: string
  isOthers?: boolean
}

type FilesDistributionByPCILabelsComponentProps = StateProps & DispatchProps & OwnProps

const FilesDistributionByPCILabels = (props: FilesDistributionByPCILabelsComponentProps) => {
  const {
    data,
    showLoader,
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    attributeSetData,
    fetchPCIFileDistribution
  } = props

  const intl = useIntl()

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  useEffect(() => {
    if (useMockData) return
    const attributeSetName = isPciDashboard
      ? PCI_ATTRIBUTE_SET_NAME_FOR_LABELS
      : HIPAA_ATTRIBUTE_SET_NAME_FOR_LABELS
    fetchPCIFileDistribution({
      attributeSetName,
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [useMockData, fetchPCIFileDistribution, widgetDashboardId, widgetDatasourceId])

  const pieChartData = useMemo((): ChartPieItem[] => {
    if (!data?.widgetData?.length) return []

    const sorted = [...data.widgetData].sort((a, b) => b.objectCount - a.objectCount)
    const top4 = sorted.slice(0, 4)
    const others = sorted.slice(4)

    const top4Data = top4.map((label, index) => ({
      key: label.id,
      name: label.name,
      value: label.objectCount,
      color: [ChartColors.Purple, ChartColors.LightPurple, ChartColors.Blue, ChartColors.LightBlue][
        index
      ]
    }))

    if (others.length > 0) {
      const othersCount = others.reduce((sum, item) => sum + item.objectCount, 0)
      top4Data.push({
        key: intl.formatMessage({ id: 'others' }),
        name: 'Others',
        value: othersCount,
        color: ChartColors.Yellow
      })
    }

    return top4Data
  }, [data, intl])

  const listData = useMemo((): ListItem[] => {
    if (!data?.widgetData?.length) return []

    const sorted = [...data.widgetData].sort((a, b) => b.objectCount - a.objectCount)
    const top4 = sorted.slice(0, 4)
    const others = sorted.slice(4)

    const top4ListData: ListItem[] = top4.map((label, index) => ({
      key: label.id,
      name: label.name,
      value: label.objectCount,
      color: [ChartColors.Purple, ChartColors.LightPurple, ChartColors.Blue, ChartColors.LightBlue][
        index
      ],
      isOthers: false
    }))

    if (others.length > 0) {
      const othersCount = others.reduce((sum, item) => sum + item.objectCount, 0)
      top4ListData.push({
        key: intl.formatMessage({ id: 'others' }),
        value: othersCount,
        name: 'Others',
        color: ChartColors.Yellow,
        isOthers: true
      })
    }

    return top4ListData
  }, [data, intl])

  const handlePieClick = (labelId: string) => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_LABEL_MANAGEMENT}${URL_LABELS}/${labelId}${URL_OVERVIEW}`,
        pageName: URL_LABELS
      })
    )
  }

  const handleListItemClick = (item: ListItem) => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_LABEL_MANAGEMENT}${URL_LABELS}/${item.key}${URL_OVERVIEW}`,
        pageName: URL_LABELS
      })
    )
  }

  const handleSummaryItemClick = (tabUrl: string) => {
    if (!attributeSetData?.id) return

    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData.id}${tabUrl}`
      })
    )
  }

  const handleViewAll = () => {
    if (!attributeSetData?.id) return

    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData.id}${URL_OVERVIEW}`
      })
    )
  }

  const cardTitle = (
    <WidgetTitle
      title={
        <div className="widget-title">
          <Typography color="primary" variant="h6" as="span">
            {intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.filesDistributionByPCILabels.title'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: isPciDashboard
                ? 'compliance.dashboard.pci.widget.title.tooltip'
                : 'compliance.dashboard.hipaa.widget.title.tooltip'
            })}
            placement="right"
          >
            <Icon size={16} name="InfoCircle" className="pointer" />
          </Tooltip>
        </div>
      }
      bordered
    />
  )

  const CustomLabelsTooltip = (props: any) => {
    const { active, payload } = props

    if (active && payload && payload.length) {
      const data = payload[0].payload

      return (
        <div className="custom-colored-tooltip xs-p-8">
          <Typography
            className="tooltip-title-with-color"
            color="primary"
            type="medium"
            variant="baseMinus2"
            as="div"
          >
            {data.name}
          </Typography>
          <Typography as="div" color="primary" variant="baseMinus2" type="medium">
            {intl.formatNumber(data.value || 0, {
              notation: 'compact'
            })}
          </Typography>
        </div>
      )
    }
    return null
  }

  const hasChartData = hasData(pieChartData)

  const emptyStateUrl = getUpdatedUrl({
    pathName: URL_INSIGHTS + URL_LABEL_MANAGEMENT + URL_LABEL_SETS
  })

  return (
    <Card
      className="files-distribution-by-pci-labels-widget"
      cardTitle={cardTitle}
      cardFooter={
        <Button
          className="card-footer"
          size={24}
          variant="link"
          icon={<Icon name="ArrowRight" />}
          iconPosition="right"
          onClick={handleViewAll}
        >
          {intl.formatMessage({ id: 'notifications.viewAll' })}
        </Button>
      }
    >
      <div className="widget-content">
        <div className="summary-section">
          <div className="summary-item" onClick={() => handleSummaryItemClick(URL_OVERVIEW)}>
            <Typography color="primary" type="strong" variant="h4">
              <FormattedNumber value={data?.totalFiles || 0} notation="compact" />
            </Typography>
            <Typography color="secondary" type="medium" variant="baseMinus2" as="span">
              {intl.formatMessage({
                id:
                  'compliance.dashboard.pci.widget.filesDistributionByPCILabels.summary.totalFiles'
              })}
            </Typography>
          </div>
          <div className="summary-item" onClick={() => handleSummaryItemClick(URL_DATA_SOURCES)}>
            <Typography color="primary" type="strong" variant="h4">
              <FormattedNumber value={data?.datasourcesCount || 0} notation="compact" />
            </Typography>
            <Typography color="secondary" type="medium" variant="baseMinus2" as="span">
              {intl.formatMessage({
                id:
                  'compliance.dashboard.pci.widget.filesDistributionByPCILabels.summary.datasources'
              })}
            </Typography>
          </div>
        </div>

        {hasChartData ? (
          <div className="chart-section">
            <Typography as="span" variant="baseMinus2">
              {intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.filesDistributionByPCILabels.chartTitle'
              })}
            </Typography>

            <div className="chart-container">
              <div className="pie-chart-wrapper">
                <ChartPie
                  data={pieChartData}
                  width={180}
                  gap={2}
                  isAnimated={true}
                  isClickable={true}
                  onClick={handlePieClick}
                  centerCountStyles={{
                    fontSize: '1.4rem',
                    fontWeight: 700
                  }}
                  customToolTip={<CustomLabelsTooltip />}
                />
              </div>

              <div className="legend">
                {listData.map((item) => (
                  <div
                    key={item.key}
                    className={`legend-item ${item.isOthers ? 'others-item' : ''}`}
                    onClick={() => handleListItemClick(item)}
                  >
                    <div className="circle" style={{ backgroundColor: item.color }} />
                    <Typography variant="baseMinus2" color="primary" as="span" className="xs-ml-4">
                      {item.name}
                    </Typography>
                    <Typography variant="baseMinus2" type="strong" className="value">
                      {intl.formatNumber(item.value, { notation: 'compact' })}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            title={intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.filesDistributionByPCILabels.errorTitle'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
            description={intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.filesDistributionByPCILabels.errorDesc'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
            icon={<EmptyStateIcon />}
            fallbackRedirectionButton={{
              url: emptyStateUrl,
              text: intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.filesDistributionByPCILabels.errorBtnText'
              })
            }}
          />
        )}
      </div>

      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  data: ownProps?.useMockData
    ? mockPCIFileDistribution.data
    : state.complianceDashboard.pciFileDistribution?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  attributeSetData: state.complianceDashboard.attributeSetData,
  showLoader: getShowLoader(ACTION_FETCH_PCI_FILE_DISTRIBUTION)
})

const mapDispatchToProps: DispatchProps = {
  fetchPCIFileDistribution
}

export default connect(mapStateToProps, mapDispatchToProps)(FilesDistributionByPCILabels)
